export type JsonContentSource = "gcs" | "local";
export type AssetContentSource = "gcs" | "local" | "project";

export interface ContentLoadMeta {
  jsonSource: JsonContentSource;
  gcsBaseUrl?: string;
  remoteUrl?: string;
  fallbackReason?: string;
  loadedAt: string;
}

export interface AssetSourceStats {
  /** CMS marketing asset — URL built from pip_GCS_url/assets/... */
  gcsUrl: number;
  /** CMS asset — bundled file in content/assets/ (no GCS or explicit local) */
  localBundled: number;
  /** UI icons / diagrams — app bundle only */
  projectApp: number;
  /** Image failed on GCS and switched to local bundled file */
  gcsFailedUsedLocal: number;
}

export interface RecentAssetEntry {
  path: string;
  source: AssetContentSource;
  url: string;
  at: string;
}

let loadMeta: ContentLoadMeta = {
  jsonSource: "local",
  loadedAt: new Date().toISOString(),
};

const stats: AssetSourceStats = {
  gcsUrl: 0,
  localBundled: 0,
  projectApp: 0,
  gcsFailedUsedLocal: 0,
};

const recentAssets: RecentAssetEntry[] = [];
const MAX_RECENT = 8;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeCmsSourceUpdates(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setContentLoadMeta(meta: ContentLoadMeta): void {
  loadMeta = meta;

  const label = meta.jsonSource === "gcs" ? "GCS (remote bucket)" : "LOCAL (bundled fallback)";
  console.info(
    [
      `[Genius CMS] JSON source: ${label}`,
      meta.remoteUrl ? `  URL: ${meta.remoteUrl}` : "",
      meta.fallbackReason ? `  Reason: ${meta.fallbackReason}` : "",
    ]
      .filter(Boolean)
      .join("\n")
  );
  notifyListeners();
}

export function getContentLoadMeta(): ContentLoadMeta {
  return loadMeta;
}

export function getAssetSourceStats(): AssetSourceStats {
  return { ...stats };
}

export function getRecentAssetEntries(): RecentAssetEntry[] {
  return [...recentAssets];
}

export function isCmsDebugEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_CMS_DEBUG === "true";
}

export function trackAssetResolution(
  path: string,
  source: AssetContentSource,
  resolvedUrl: string
): void {
  if (source === "gcs") stats.gcsUrl += 1;
  else if (source === "local") stats.localBundled += 1;
  else stats.projectApp += 1;

  if (path) {
    recentAssets.unshift({
      path,
      source,
      url: resolvedUrl,
      at: new Date().toISOString(),
    });
    if (recentAssets.length > MAX_RECENT) recentAssets.pop();
  }

  if (isCmsDebugEnabled()) {
    console.info(`[Genius CMS] Asset [${source}] ${path}`, resolvedUrl);
  }

  notifyListeners();
}

export function trackAssetGcsFallback(path: string, localUrl: string): void {
  stats.gcsFailedUsedLocal += 1;
  console.warn(`[Genius CMS] Asset GCS failed → local fallback: ${path}`, localUrl);
  notifyListeners();
}

export function resetAssetSourceStats(): void {
  stats.gcsUrl = 0;
  stats.localBundled = 0;
  stats.projectApp = 0;
  stats.gcsFailedUsedLocal = 0;
  recentAssets.length = 0;
  notifyListeners();
}
